import { useState, useEffect } from 'react';
import { statusShort, getStatusColor } from './StatusPicker';

// Сводная прогноза (read-only): дерево BU→ТМ→СВ→Маршрут, недели колонками + Итог.
// Ячейка недели = Прогноз/Заказ, цвет по %. Клик по маршруту раскрывает прогноз по дням.
// roots: [{ key, label, routes:[{id,...}], children?:[node] }]
// loadRouteDays(routeId) => Promise<{ 'YYYY-MM-DD': {order,plan,fact} }>
const DOW_RU = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
const SL_TARGET = 95;

export default function ForecastPivot({ roots, weekNums, rweeks, weeks, loadRouteDays, focusRouteId, srcKey }) {
  const [open, setOpen] = useState({});
  const [days, setDays] = useState({});   // routeId -> byDate | 'loading'
  const toggle = k => setOpen(o => ({ ...o, [k]: !o[k] }));

  // При смене источника (Заморожено↔Актуально) сбрасываем кэш дней и сворачиваем
  // открытые дневные панели — иначе показываются старые данные из прошлого режима.
  useEffect(() => {
    setDays({});
    setOpen(o => { const n = {}; for (const k in o) if (!k.startsWith('R:')) n[k] = o[k]; return n; });
  }, [srcKey]);

  const sumFor = (routes) => {
    const per = {}; let tPlan = 0, tOrd = 0;
    weekNums.forEach(w => {
      let plan = 0, ord = 0;
      routes.forEach(r => { const c = rweeks[r.id]?.[w]; if (c) { plan += c.plan || 0; ord += c.ord || 0; } });
      per[w] = { plan, ord };
      tPlan += plan; tOrd += ord;
    });
    return { per, tPlan, tOrd, pct: tOrd ? Math.round(tPlan / tOrd * 100) : null };
  };

  async function openRoute(routeId, key) {
    const isOpen = open[key];
    toggle(key);
    if (!isOpen && !days[routeId] && loadRouteDays) {
      setDays(d => ({ ...d, [routeId]: 'loading' }));
      try { const byDate = await loadRouteDays(routeId); setDays(d => ({ ...d, [routeId]: byDate })); }
      catch { setDays(d => ({ ...d, [routeId]: {} })); }
    }
  }

  // Автораскрытие ветки до нужного маршрута (переход из алёрта)
  useEffect(() => {
    if (!focusRouteId) return;
    const path = {};
    const walk = (node, chain) => {
      const has = node.routes?.some(r => r.id === focusRouteId);
      if (!has) return false;
      if (node.children) { for (const c of node.children) if (walk(c, [...chain, node.key])) break; }
      chain.concat(node.key).forEach(k => { path[k] = true; });
      if (!node.children) path[`R:${focusRouteId}`] = true;
      return true;
    };
    roots.forEach(r => walk(r, []));
    setOpen(o => ({ ...o, ...path }));
    setTimeout(() => document.getElementById(`fp-${focusRouteId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 200);
  }, [focusRouteId, roots]);

  const colW = 52, itogW = 66, labelW = 190;
  const cellColor = (plan, ord) => {
    if (!ord) return { c: 'var(--text-light)', b: 'transparent' };
    const p = Math.round(plan / ord * 100);
    return p >= SL_TARGET ? { c: '#137a54', b: '#E4F2EA' } : { c: '#b23', b: '#FDECE8' };
  };

  const renderNode = (node, depth) => {
    const s = sumFor(node.routes);
    const isRoute = !node.children;
    const k = node.key;
    const expandable = (node.children && node.children.length) || isRoute;
    const rows = [];
    rows.push(
      <div key={k} id={isRoute ? `fp-${node.routeId}` : undefined}
        onClick={() => isRoute ? openRoute(node.routeId, `R:${node.routeId}`) : toggle(k)}
        style={{ display: 'flex', alignItems: 'stretch', borderBottom: '1px solid var(--border)', cursor: expandable ? 'pointer' : 'default',
          background: depth === 0 ? '#EEF3FA' : node.routeId === focusRouteId ? '#FFF7DA' : '#fff' }}>
        <div style={{ width: labelW, minWidth: labelW, padding: '6px 8px', paddingLeft: 8 + depth * 14, fontSize: 12, fontWeight: depth <= 1 ? 700 : 500, display: 'flex', alignItems: 'flex-start', gap: 6, position: 'sticky', left: 0, background: 'inherit', zIndex: 1 }}>
          {expandable && <span style={{ fontSize: 9, color: 'var(--text-muted)', width: 8, marginTop: 2 }}>{(isRoute ? open[`R:${node.routeId}`] : open[k]) ? '▼' : '▶'}</span>}
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.label}</span>
            {node.sub && <span style={{ display: 'block', fontSize: 9, fontWeight: 400, color: 'var(--text-light)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.sub}</span>}
          </span>
        </div>
        {weekNums.map(w => {
          const { plan, ord } = s.per[w]; const col = cellColor(plan, ord);
          const pct = ord ? Math.round(plan / ord * 100) : null;
          return <div key={w} style={{ width: colW, minWidth: colW, textAlign: 'center', fontSize: 11, padding: '5px 2px', background: col.b, color: col.c, fontVariantNumeric: 'tabular-nums' }}>
            <div>{plan}<span style={{ color: 'var(--text-light)' }}>/{ord}</span></div>
            <div style={{ fontSize: 9, fontWeight: 700 }}>{pct == null ? '—' : pct + '%'}</div>
          </div>;
        })}
        <div style={{ width: itogW, minWidth: itogW, textAlign: 'center', fontSize: 11, padding: '7px 2px', fontWeight: 700, background: '#F4F7FB' }}>
          {s.tPlan}<span style={{ color: 'var(--text-light)', fontWeight: 400 }}>/{s.tOrd}</span>
          <div style={{ fontSize: 10, color: s.pct == null ? 'var(--text-light)' : s.pct >= SL_TARGET ? '#137a54' : '#b23' }}>{s.pct == null ? '—' : s.pct + '%'}</div>
        </div>
      </div>
    );
    if (!isRoute && open[k]) node.children.forEach(c => rows.push(...renderNode(c, depth + 1)));
    if (isRoute && open[`R:${node.routeId}`]) rows.push(renderRouteDays(node, depth));
    return rows;
  };

  function renderRouteDays(node, depth) {
    const byDate = days[node.routeId];
    return (
      <div key={`d-${node.routeId}`} style={{ padding: '6px 8px 10px', paddingLeft: 8 + (depth + 1) * 14, background: '#FBFCFE', borderBottom: '1px solid var(--border)' }}>
        {byDate === 'loading' || byDate == null ? (
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Загрузка прогноза по дням…</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            {weekNums.map(w => {
              const wd = (weeks[w]?.days || []);
              if (!wd.length) return null;
              return (
                <div key={w} style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                  <div style={{ width: 40, fontSize: 10, fontWeight: 700, color: 'var(--text-muted)' }}>W{w}</div>
                  {wd.map(({ day, dow, date }) => {
                    const c = byDate[date] || {};
                    const plan = c.plan;
                    const st = c.code ? statusShort(c.code) : null;
                    const sc = c.code ? getStatusColor(c.code) : null;
                    return <div key={date} style={{ width: 40, textAlign: 'center', fontSize: 10 }}>
                      <div style={{ color: 'var(--text-light)', fontSize: 8 }}>{DOW_RU[dow]}<br/>{day}</div>
                      <div style={{ borderRadius: 4, padding: '2px 1px', background: sc ? sc.bg : '#F1F5F9', color: sc ? sc.fg : 'var(--text-light)', fontWeight: 700, lineHeight: 1.1 }}>
                        <div style={{ fontSize: 9 }}>{st || '·'}</div>
                        <div style={{ fontSize: 10 }}>{plan == null ? '' : plan}</div>
                      </div>
                    </div>;
                  })}
                </div>
              );
            })}
            <div style={{ fontSize: 9, color: 'var(--text-light)', marginTop: 2 }}>Числа — плановые визиты по дню; редактируется на роли СВ.</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: labelW + colW * weekNums.length + itogW }}>
          {/* Общее выполнение по всем строкам: над номерами недель */}
          {(() => {
            const seen = new Set(); const all = [];
            roots.forEach(r => (r.routes || []).forEach(x => { if (!seen.has(x.id)) { seen.add(x.id); all.push(x); } }));
            const t = sumFor(all);
            const colr = (plan, ord) => { const c = cellColor(plan, ord); return c; };
            return (
              <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: '#F4F7FB' }}>
                <div style={{ width: labelW, minWidth: labelW, padding: '6px 8px', fontSize: 11, fontWeight: 700, color: '#1F3864', position: 'sticky', left: 0, background: '#F4F7FB', zIndex: 2 }}>Общее выполнение</div>
                {weekNums.map(w => {
                  const { plan, ord } = t.per[w]; const col = colr(plan, ord);
                  const pct = ord ? Math.round(plan / ord * 100) : null;
                  return <div key={w} style={{ width: colW, minWidth: colW, textAlign: 'center', fontSize: 11, padding: '4px 2px', fontWeight: 700, color: col.c, fontVariantNumeric: 'tabular-nums' }}>
                    <div style={{ fontSize: 13 }}>{pct == null ? '—' : pct + '%'}</div>
                    <div style={{ fontSize: 9, fontWeight: 400, color: 'var(--text-light)' }}>{plan}/{ord}</div>
                  </div>;
                })}
                <div style={{ width: itogW, minWidth: itogW, textAlign: 'center', fontSize: 11, padding: '4px 2px', fontWeight: 700, color: t.pct == null ? 'var(--text-light)' : t.pct >= SL_TARGET ? '#137a54' : '#b23' }}>
                  <div style={{ fontSize: 13 }}>{t.pct == null ? '—' : t.pct + '%'}</div>
                  <div style={{ fontSize: 9, fontWeight: 400, color: 'var(--text-light)' }}>{t.tPlan}/{t.tOrd}</div>
                </div>
              </div>
            );
          })()}
          {/* Шапка */}
          <div style={{ display: 'flex', borderBottom: '2px solid var(--border)', background: '#1F3864', color: '#fff' }}>
            <div style={{ width: labelW, minWidth: labelW, padding: '8px', fontSize: 11, fontWeight: 700, position: 'sticky', left: 0, background: '#1F3864', zIndex: 2 }}>Прогноз / Заказ</div>
            {weekNums.map(w => <div key={w} style={{ width: colW, minWidth: colW, textAlign: 'center', padding: '8px 2px', fontSize: 11, fontWeight: 700 }}>W{w}</div>)}
            <div style={{ width: itogW, minWidth: itogW, textAlign: 'center', padding: '8px 2px', fontSize: 11, fontWeight: 700 }}>Итог · %</div>
          </div>
          {roots.map(r => renderNode(r, 0))}
        </div>
      </div>
    </div>
  );
}
